import { StockDataPoint } from '../data/mockStockData';

export interface EMADataPoint {
  timestamp: Date;
  value: number;
}

export interface TechnicalIndicators {
  ema12: EMADataPoint[];
  ema26: EMADataPoint[];
  ema50: EMADataPoint[];
  ema200: EMADataPoint[];
  macd: EMADataPoint[];
  macdSignal: EMADataPoint[];
  macdHistogram: EMADataPoint[];
}

/**
 * Calculate Exponential Moving Average (EMA)
 * @param data Array of stock data points
 * @param period EMA period (e.g., 12, 26, 50, 200)
 * @returns Array of EMA data points
 */
export const calculateEMA = (data: StockDataPoint[], period: number): EMADataPoint[] => {
  if (data.length === 0 || period <= 0) return [];

  const emaData: EMADataPoint[] = [];
  const multiplier = 2 / (period + 1);

  // First EMA value is the SMA of the first 'period' values
  let sum = 0;
  for (let i = 0; i < Math.min(period, data.length); i++) {
    sum += data[i].close;
  }

  if (data.length < period) {
    // If we don't have enough data points, start with available data
    emaData.push({
      timestamp: data[0].timestamp,
      value: sum / Math.min(period, data.length)
    });
  } else {
    emaData.push({
      timestamp: data[period - 1].timestamp,
      value: sum / period
    });
  }

  // Calculate subsequent EMA values
  const startIndex = data.length < period ? 1 : period;
  for (let i = startIndex; i < data.length; i++) {
    const currentPrice = data[i].close;
    const previousEMA = emaData[emaData.length - 1].value;
    const currentEMA = (currentPrice * multiplier) + (previousEMA * (1 - multiplier));

    emaData.push({
      timestamp: data[i].timestamp,
      value: currentEMA
    });
  }

  return emaData;
};

/**
 * Calculate Simple Moving Average (SMA)
 * @param data Array of stock data points
 * @param period SMA period
 * @returns Array of SMA data points
 */
export const calculateSMA = (data: StockDataPoint[], period: number): EMADataPoint[] => {
  if (data.length === 0 || period <= 0) return [];

  const smaData: EMADataPoint[] = [];

  for (let i = period - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sum += data[j].close;
    }

    smaData.push({
      timestamp: data[i].timestamp,
      value: sum / period
    });
  }

  return smaData;
};

/**
 * Calculate MACD (Moving Average Convergence Divergence)
 * @param data Array of stock data points
 * @returns MACD line, signal line, and histogram
 */
export const calculateMACD = (data: StockDataPoint[]): {
  macd: EMADataPoint[];
  signal: EMADataPoint[];
  histogram: EMADataPoint[];
} => {
  const ema12 = calculateEMA(data, 12);
  const ema26 = calculateEMA(data, 26);

  // MACD line is the difference between 12-day EMA and 26-day EMA
  const macdLine: EMADataPoint[] = [];
  const minLength = Math.min(ema12.length, ema26.length);

  for (let i = 0; i < minLength; i++) {
    macdLine.push({
      timestamp: ema12[i].timestamp,
      value: ema12[i].value - ema26[i].value
    });
  }

  // Signal line is 9-day EMA of MACD line
  const signalLine = calculateEMAFromValues(macdLine, 9);

  // Histogram is the difference between MACD line and signal line
  const histogram: EMADataPoint[] = [];
  const histMinLength = Math.min(macdLine.length, signalLine.length);

  for (let i = 0; i < histMinLength; i++) {
    histogram.push({
      timestamp: macdLine[i].timestamp,
      value: macdLine[i].value - signalLine[i].value
    });
  }

  return {
    macd: macdLine,
    signal: signalLine,
    histogram
  };
};

/**
 * Calculate EMA from existing EMA data points
 * @param data Array of EMA data points
 * @param period EMA period
 * @returns Array of EMA data points
 */
const calculateEMAFromValues = (data: EMADataPoint[], period: number): EMADataPoint[] => {
  if (data.length === 0 || period <= 0) return [];

  const emaData: EMADataPoint[] = [];
  const multiplier = 2 / (period + 1);

  // First EMA value is the SMA of the first 'period' values
  let sum = 0;
  for (let i = 0; i < Math.min(period, data.length); i++) {
    sum += data[i].value;
  }

  if (data.length < period) {
    emaData.push({
      timestamp: data[0].timestamp,
      value: sum / Math.min(period, data.length)
    });
  } else {
    emaData.push({
      timestamp: data[period - 1].timestamp,
      value: sum / period
    });
  }

  const startIndex = data.length < period ? 1 : period;
  for (let i = startIndex; i < data.length; i++) {
    const currentValue = data[i].value;
    const previousEMA = emaData[emaData.length - 1].value;
    const currentEMA = (currentValue * multiplier) + (previousEMA * (1 - multiplier));

    emaData.push({
      timestamp: data[i].timestamp,
      value: currentEMA
    });
  }

  return emaData;
};

/**
 * Calculate all technical indicators for a stock
 * @param data Array of stock data points
 * @returns Object containing all technical indicators
 */
export const calculateTechnicalIndicators = (data: StockDataPoint[]): TechnicalIndicators => {
  const ema12 = calculateEMA(data, 12);
  const ema26 = calculateEMA(data, 26);
  const ema50 = calculateEMA(data, 50);
  const ema200 = calculateEMA(data, 200);

  const macdData = calculateMACD(data);

  return {
    ema12,
    ema26,
    ema50,
    ema200,
    macd: macdData.macd,
    macdSignal: macdData.signal,
    macdHistogram: macdData.histogram
  };
};

/**
 * Filter data by date range
 * @param data Array of stock data points
 * @param startDate Start date
 * @param endDate End date
 * @returns Filtered array of stock data points
 */
export const filterDataByDateRange = (
  data: StockDataPoint[],
  startDate: Date,
  endDate: Date
): StockDataPoint[] => {
  return data.filter(point => 
    point.timestamp >= startDate && point.timestamp <= endDate
  );
};

/**
 * Get date range presets
 * @param currentDate Current date (default: today)
 * @returns Object with date range presets
 */
export const getDateRangePresets = (currentDate: Date = new Date()) => {
  const today = new Date(currentDate);
  
  const fiveDaysAgo = new Date(today);
  fiveDaysAgo.setDate(today.getDate() - 5);
  
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(today.getMonth() - 3);
  
  const ytdDate = new Date(today.getFullYear(), 0, 1);
  
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  
  const threeYearsAgo = new Date(today);
  threeYearsAgo.setFullYear(today.getFullYear() - 3);
  
  const fiveYearsAgo = new Date(today);
  fiveYearsAgo.setFullYear(today.getFullYear() - 5);

  const tenYearsAgo = new Date(today);
  tenYearsAgo.setFullYear(today.getFullYear() - 10);

  return {
    '5D': { start: fiveDaysAgo, end: today, label: '5D' },
    '1M': { start: oneMonthAgo, end: today, label: '1M' },
    '3M': { start: threeMonthsAgo, end: today, label: '3M' },
    'YTD': { start: ytdDate, end: today, label: 'YTD' },
    '1Y': { start: oneYearAgo, end: today, label: '1Y' },
    '3Y': { start: threeYearsAgo, end: today, label: '3Y' },
    '5Y': { start: fiveYearsAgo, end: today, label: '5Y' },
    '10Y': { start: tenYearsAgo, end: today, label: '10Y' },
    'ALL': { start: new Date('2010-01-01'), end: today, label: 'All' }
  };
};



